import { useEffect, useRef, useState } from 'react'
import { LuHistory } from 'react-icons/lu'

import { FeatureFlag, WhenFlag } from '@/feature/feature-flags'
import { MapEmbedding, MapHandler } from '@/feature/map'
import { LayerManager } from '@/feature/map/components/LayerManager'
import { MapIconLegend } from '@/feature/map/components/MapIconLegend'
import { useReplayConfig } from '@/feature/observatory'
import { TaskAssetEnRouteLine } from '@/feature/observatory/components/TaskAssetEnRouteLine'
import { TaskMapOverlay } from '@/feature/observatory/components/TaskMapOverlay/TaskMapOverlay'
import { Replay, ReplayMapMessage } from '@/feature/replay'

import { Tooltip } from '@/lib/core/components/Tooltip/Tooltip'

import { TaskFocusMapProps } from './TaskFocusMap.types'
import {
  getInitialMapCenter,
  INITIAL_MAP_ZOOM,
  TASK_FOCUS_MAP_OPTIONS,
} from '../TaskFocusModal.utils'
import { TaskVehicleMarker } from '../TaskVehicleMarker'
import styles from './TaskFocusMap.module.css'

/**
 * The task focus modal's map pane: one task's route, its assigned vehicle, and nothing else.
 *
 * This is a second, independent map instance — safe and cheap because MapHandler builds its own
 * google.maps.Map and MapContext, and the Maps API script is loaded once globally (the Execution
 * Board already wraps its tree in GoogleLoader). The Observatory's weight lives entirely in the
 * children it mounts — clustered fleet markers, the geofence layer, the layer manager, traffic —
 * none of which are here.
 *
 * Notably it does NOT pass `mapCallback`: that would publish this map into the Observatory page
 * store's single `map` slot and hijack the Observatory's own search marker and ETA panel.
 */
export const TaskFocusMap = ({ task, replay }: TaskFocusMapProps) => {
  const replaySettings = useReplayConfig()

  // The modal is a native <dialog>, and React runs child effects before the parent's showModal().
  // Until that call the dialog is display:none, so constructing the map any earlier builds it
  // against a 0x0 container: Google measures its tile viewport once at that size and renders bare
  // background below the fold, and the route's bounds fit reads an empty rect and picks a garbage
  // zoom.
  //
  // Hence a ResizeObserver rather than requestAnimationFrame: rAF callbacks run BEFORE the frame's
  // layout, so one would still observe the pre-showModal() box. ResizeObserver reports sizes after
  // layout, so waiting for a non-zero box is what actually guarantees a laid-out container.
  const paneRef = useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const pane = paneRef.current
    if (!pane) return

    const observer = new ResizeObserver((entries) => {
      const box = entries[entries.length - 1]?.contentRect
      if (box && box.width > 0 && box.height > 0) {
        setIsReady(true)
      }
    })
    observer.observe(pane)

    return () => observer.disconnect()
  }, [])

  // aria-disabled rather than disabled: a disabled button receives no pointer events, so the
  // tooltip explaining why replay is unavailable would never open.
  const replayButton = (
    <button
      type="button"
      className={styles.replayButton}
      onClick={replay.startReplay}
      aria-disabled={replay.replayUnavailableReason != null}
      data-testid="task-focus-replay-button"
    >
      <LuHistory size={16} />
      <span>Replay</span>
    </button>
  )

  return (
    <div ref={paneRef} className={styles.mapPane} data-testid="task-focus-map">
      {replay.isReplayActive ? (
        <ReplayMapMessage
          assetId={replay.replayAsset?.id ?? 0}
          onClose={replay.endReplay}
          selectionStart={replay.selectionStartTime}
          selectionEnd={replay.selectionEndTime}
        />
      ) : null}

      {replay.replayTrailMessage ? (
        <div className={styles.trailStatus} role="status" data-testid="task-focus-trail-status">
          {replay.replayTrailMessage}
        </div>
      ) : null}

      {isReady && (
        <MapHandler
          center={getInitialMapCenter(task)}
          zoom={INITIAL_MAP_ZOOM}
          mapOptions={TASK_FOCUS_MAP_OPTIONS}
        >
          {/* Entered from the map here rather than an asset panel, so it goes in the control flow
              along the top edge — clear of the layer and legend buttons down the right. */}
          {!replay.isReplayActive && replay.canReplay ? (
            <FeatureFlag flag="featureReplay">
              <WhenFlag>
                <MapEmbedding position={window.google?.maps.ControlPosition?.TOP_RIGHT}>
                  {replay.replayUnavailableReason ? (
                    <Tooltip tooltipContent={replay.replayUnavailableReason} placement="bottom">
                      {replayButton}
                    </Tooltip>
                  ) : (
                    replayButton
                  )}
                </MapEmbedding>
              </WhenFlag>
            </FeatureFlag>
          ) : null}

          {/* The same controls the observatory carries, minus the layers this map never draws. */}
          <LayerManager toggles={{ traffic: true, satellite: true }} />
          <MapIconLegend />

          {/* No onViewGeofence — this map draws no geofence layer, so the action isn't offered. */}
          <TaskMapOverlay task={task} />
          <TaskVehicleMarker task={task} />
          <TaskAssetEnRouteLine task={task} />
          {/* The planned route stays drawn underneath, so the history can be read against it. */}
          {replay.replayAsset ? (
            <Replay
              asset={replay.replayAsset}
              startDate={replay.selectionStartTime}
              endDate={replay.selectionEndTime}
              stepSize={`${replay.stepSize}m`}
              layers={replaySettings.route.layers}
              history={replay.replayHistory}
            />
          ) : null}
        </MapHandler>
      )}
    </div>
  )
}
